import type {
  ApiErrorPayload,
  HealthResponse,
  SaveConfigResponse,
  StatsResponse,
  TestConfig
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly requestId?: string
  ) {
    super(message);
  }
}

export class ControlApi {
  private readonly baseUrl: string;

  constructor(baseUrl: string, private readonly adminToken: string) {
    this.baseUrl = normalizeBaseUrl(baseUrl);
  }

  get healthUrl(): string {
    return `${this.baseUrl}/health`;
  }

  async health(): Promise<HealthResponse> {
    return this.request<HealthResponse>("/health", {}, false);
  }

  async getConfig(): Promise<TestConfig> {
    return this.request<TestConfig>("/admin/config");
  }

  async saveConfig(config: Omit<TestConfig, "updatedAtUtc">): Promise<TestConfig> {
    const response = await this.request<SaveConfigResponse>("/admin/config", {
      method: "POST",
      body: JSON.stringify(config)
    });

    return response.config;
  }

  async getStats(): Promise<StatsResponse> {
    return this.request<StatsResponse>("/admin/stats");
  }

  private async request<T>(
    path: string,
    init: RequestInit = {},
    authenticate = true
  ): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");

    if (init.body !== undefined) {
      headers.set("content-type", "application/json");
    }
    if (authenticate) {
      headers.set("authorization", `Bearer ${this.adminToken}`);
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15_000);

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        headers,
        signal: controller.signal,
        cache: "no-store"
      });

      const requestId = response.headers.get("x-request-id") ?? undefined;
      const payload = await readPayload(response);

      if (!response.ok) {
        const errorPayload = payload as ApiErrorPayload;
        throw new ApiError(
          errorPayload.error?.message ??
            `API isteği ${response.status} durumuyla sonuçlandı.`,
          response.status,
          errorPayload.error?.requestId ?? requestId
        );
      }

      return payload as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new ApiError("API isteği zaman aşımına uğradı.", 0);
      }
      throw new ApiError(
        error instanceof Error
          ? `API bağlantısı kurulamadı: ${error.message}`
          : "API bağlantısı kurulamadı.",
        0
      );
    } finally {
      window.clearTimeout(timeout);
    }
  }
}

export function normalizeBaseUrl(value: string): string {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    throw new ApiError("Geçerli bir API adresi girin.", 0);
  }

  const secure = url.protocol === "https:";
  const local =
    url.protocol === "http:" &&
    (url.hostname === "localhost" ||
      url.hostname === "127.0.0.1" ||
      url.hostname === "[::1]");

  if (!secure && !local) {
    throw new ApiError(
      "API adresi HTTPS kullanmalı; HTTP yalnızca localhost için geçerlidir.",
      0
    );
  }

  url.pathname = url.pathname.replace(/\/+$/, "");
  url.search = "";
  url.hash = "";
  return url.toString().replace(/\/$/, "");
}

async function readPayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError(
      "API geçerli JSON döndürmedi.",
      response.status,
      response.headers.get("x-request-id") ?? undefined
    );
  }
}
